import { Drawer, Popover }                 from "@mantine/core";
import { useRouter }                       from "next/router";
import { forwardRef, useEffect, useState } from "react";

import { useGetLoggedIn }                     from "_common/hooks/auth";
import { useGetDeviceType, useSetLoginPopup } from "_common/hooks/global";
import toast                                  from "_common/toast";
import { shortenNumber }                      from "_common/utils";
import Icon                                   from "_components/atoms/icon";
import Typography                             from "_components/atoms/typography";
import ReportFollowModal                      from "_components/molecules/reportFollowModal";
import ShareModal                             from "_components/molecules/sharePopper/shareModal";
import { postLikeOnFeed }                     from "_services/api/feed";

import ReelsCommentsContainer from "../commentsAndLikesPopover";

import useStyles from "./styles";

function ControlsContainer ( {
	likeCount,
	shareCount,
	reel,
	updateFeedList,
	setIsReelLiked,
	isReelLiked
} ) {
	const [ likes, setLikes ]                 = useState ( likeCount );
	const [ commentCount, setCommentCount ]   = useState ( reel?.commentCount || 0 );
	const [ openShareDialog, setShareDialog ] = useState ( false );
	const deviceType                          = useGetDeviceType ();
	const isMobile                            = deviceType === "mobile";

	const isLoggedIn    = useGetLoggedIn ();
	const setLoginPopup = useSetLoginPopup ();
	const { classes }   = useStyles ();

	function handleModalClose () {
		// todo
	}
	const onClickLike = () => {
		if ( !isLoggedIn ) {
			return setLoginPopup ( true );
		}
		if ( reel.isReactedOnFeed )
			return;

		sendReactionsOnVideo ( reel.id );
	};

	const sendReactionsOnVideo = async id => {
		try {
			await postLikeOnFeed ( { feedId: id } );

			setIsReelLiked ( true );

		} catch ( err ) {
			setIsReelLiked ( false );
			toast.error ( err.displayMsg );
		}
	};

	function updateDeletedPost () {
		// todo
	}

	return (
		<div className = { classes.controlsRoot }>

			<div className = { classes.controlsWrapper }>

				<div className = { classes.controlsWrapperInner }>

					<LikeBtnWrapper
						isMobile      = { isMobile }
						isReelLiked   = { isReelLiked }
						likes         = { likes }
						onClickLike   = { onClickLike }
						reelId        = { reel.id }
						setLikes      = { setLikes }
						shortenNumber = { shortenNumber }
					/>

					<CommentsBtnWrapper
						commentCount    = { commentCount }
						isMobile        = { isMobile }
						likes           = { likes }
						reel            = { reel }
						setCommentCount = { setCommentCount }
					/>

					<ShareBtnWrapper
						isMobile        = { isMobile }
						openShareDialog = { openShareDialog }
						setShareDialog  = { setShareDialog }
						shareCount      = { shareCount }
						shortenNumber   = { shortenNumber }
					/>
				</div>

				<div className = { classes.reportFollowModalWrapper }>
					<ReportFollowModal
						feedId            = { reel.id }
						feedType          = { reel?.feedType?.[0] }
						from              = { "reel" }
						handleModalClose  = { handleModalClose }
						isFollowing       = { reel.actorDetails.isFolliowing }
						updateDeletedPost = { updateDeletedPost }
						updateFeedList    = { updateFeedList }
						userId            = { reel.actorDetails.id }
						userName          = { reel.actorDetails.name }
						verticalFeedType  = { reel.verticalFeedType }
					/>

				</div>

			</div>

			<ShareModal
				key              = { reel.id }
				handleClose      = { () => setShareDialog ( false ) }
				onClickShareItem = { () => setShareDialog ( false ) }
				open             = { openShareDialog }
				shareLink        = { window.location.href }
				title            = { reel.title }
			/>

		</div>
	);
}

const CommentsIcon = forwardRef ( ( props, ref ) => {
	const {
		commentCount,
		setCommentDialog,
		isMobile,
		openCommentsDialog
	} = props;

	const { classes } = useStyles ();

	return (
		<div ref   = { ref }
			className = { classes.iconWrapper }
			onClick   = { () => setCommentDialog ( o => !o ) }
		>
			<button
				className   = { classes.controlsIcon }
				data-active = { openCommentsDialog || undefined }
			>
				<Icon
					name = { "comment" }
					size = { isMobile ? 28 : 24 }
				/>
			</button>

			<Typography
				className = { classes.text }
				title     = { commentCount ? shortenNumber ( commentCount ) : "0" }
			/>
		</div>
	);
} );

CommentsIcon.displayName = "commentsIcon";

function CommentsBtnWrapper ( { reel, isMobile, likes, commentCount, setCommentCount } ) {
	const { classes } = useStyles ();
	const deviceType  = useGetDeviceType ();

	const [ openCommentsDialog, setCommentDialog ] = useState ( false );
	const router                                   = useRouter ();

	const updateCommentCount = () => {
		setCommentCount ( prev => {
			return prev + 1;
		} );
	};

	useEffect ( () => {
		if ( router.query.id !== reel.id ) {
			setCommentDialog ( false );

			return;
		}
	}, [ router.query.id ] );

	function onClose () {
		setCommentDialog ( false );
	}

	if ( deviceType === "mobile" )
		return (
			<>
				<CommentsIcon
					commentCount       = { commentCount }
					isMobile           = { isMobile }
					openCommentsDialog = { openCommentsDialog }
					setCommentDialog   = { setCommentDialog }
				/>

				<Drawer
					classNames      = { { content: classes.commentDrawerContent } }
					keepMounted     = { false }
					onClose         = { onClose }
					opened          = { openCommentsDialog }
					position        = { "bottom" }
					withCloseButton = { false }
				>
					<ReelsCommentsContainer
						commentCount       = { commentCount }
						likesCount         = { likes }
						onClose            = { onClose }
						open               = { openCommentsDialog }
						reel               = { reel }
						updateCommentCount = { updateCommentCount }
					/>
				</Drawer>
			</>
		);

	return (
		<Popover
			classNames   = { {
				dropdown: classes.popoverDropdown
			} }
			keepMounted  = { true }
			middlewares  = { {
				flip   : false,
				shift  : true,
				inline : true
			} }
			offset       = { {
				mainAxis  : deviceType === "desktop" ? -30 : -100,
				crossAxis : 400
			} }
			onChange     = { onClose }
			opened       = { openCommentsDialog }
			position     = "top-start"
			shadow       = "md"
			width        = { deviceType === "desktop" ? "25vw" : "45vw" }
			withinPortal = { true }
			zIndex       = { 1000 }
		>
			<Popover.Target>
				<CommentsIcon
					commentCount       = { commentCount }
					isMobile           = { isMobile }
					openCommentsDialog = { openCommentsDialog }
					setCommentDialog   = { setCommentDialog }
				/>
			</Popover.Target>

			<Popover.Dropdown>
				<ReelsCommentsContainer
					commentCount       = { commentCount }
					likesCount         = { likes }
					onClose            = { onClose }
					open               = { openCommentsDialog }
					reel               = { reel }
					updateCommentCount = { updateCommentCount }
				/>
			</Popover.Dropdown>
		</Popover>
	);
}

function LikeBtnWrapper ( { onClickLike, isReelLiked, likes, setLikes, shortenNumber, reelId, isMobile } ) {

	const { classes } = useStyles ();

	const setLoginPopup = useSetLoginPopup ();
	const isLoggedIn    = useGetLoggedIn ();

	const onClickLikeBtn = () => {
		if ( !isLoggedIn ) {

			setLoginPopup ( true );

			return;
		}

		if ( !isReelLiked ) {
			setLikes ( p => p + 1 );
		}

		onClickLike ( reelId );
	};

	return (
		<div className = { classes.iconWrapper }
			onClick       = { onClickLikeBtn }
		>
			<button
				className  = { classes.controlsIcon }
				data-liked = { isReelLiked }
			>
				<Icon
					name            = { isReelLiked ? "heartFilled" : "heartInactive" }
					sameInBothTheme = { isReelLiked || isMobile ? true : false }
					size            = { isMobile ? 28 : 24 }
				/>
			</button>

			<Typography className = { classes.text }
				title                = { likes ? shortenNumber ( likes ) : "0" }
			/>
		</div>
	);
}

function ShareBtnWrapper ( { setShareDialog, shareCount, shortenNumber, isMobile, openShareDialog } ) {
	const { classes } = useStyles ();

	return (
		<div className = { classes.iconWrapper }
			onClick       = { () => setShareDialog ( true ) }
		>
			<button
				className   = { classes.controlsIcon }
				data-active = { openShareDialog || undefined }
			>
				<Icon
					name            = { "reels-share" }
					sameInBothTheme = { isMobile ? true : false }
					size            = { isMobile ? 28 : 24 }
				/>

			</button>

			<Typography
				className = { classes.text }
				title     = { shareCount ? shortenNumber ( shareCount ) : "0" }
			/>
		</div>
	);
}
export default ControlsContainer;
